import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Head } from '@inertiajs/react';
import AppLayout from '../AppLayout';
import InlineEditableField from '@/components/form/fields/InlineEditableField';
import NoteContent from '@/components/notes/NoteContent';
import { useGetNote } from '@/api/notes/useGetNote';
import { useSaveNote } from '@/api/notes/useSaveNote';
import { useLoadVersionContent } from '@/api/notes/useLoadVersionContent';
import { useNoteTab } from '@/api/notes/useNoteTab';
import { Spinner } from '@/components/ui';
import { NoteProvider } from '@/components/notes/context/NoteContext';
import BottomBar from '@/components/notes/bottom-bar/BottomBar';
import NoteError from '@/components/notes/NoteError';
import NoteTagsDisplay from '@/components/notes/tags/NoteTagsDisplay';
import { resolveNoteKey } from '@/services/NoteKeyResolver';
import VersionsPanel from '@/components/notes/versions/VersionsPanel';
import { useFullscreenStore } from '@/store/fullscreenStore';
import { useTranslation } from 'react-i18next';
import NoteExportMenu from '@/components/notes/export/NoteExportMenu';
import type { NoteExportSnapshot } from '@/components/notes/export/types';

const Note = () => {
  const { t } = useTranslation();
  const uuid = window.location.pathname.split('/').pop();
  const { note, hasKeyWrapper, loading, error, requestUnlock, completeEncryptionConversion } = useGetNote(uuid);

  const [toolbarPortalTarget, setToolbarPortalTarget] = useState<HTMLDivElement | null>(null);
  const [headerPortalTarget, setHeaderPortalTarget] = useState<HTMLElement | null>(null);
  const [subheaderStickyPortal, setSubheaderStickyPortal] = useState<HTMLElement | null>(null);
  const [isVersionsPanelOpen, setIsVersionsPanelOpen] = useState(false);
  const [viewingVersion, setViewingVersion] = useState<number | null>(null);
  const [isEncryptionConversionActive, setEncryptionConversionActive] = useState(false);

  const isReadOnly = viewingVersion !== null;

  const { versionContent, versionCreatedAt, loading: versionLoading, error: versionError } = useLoadVersionContent(
    isReadOnly ? (note?.id ?? null) : null,
    viewingVersion,
  );

  const { title, setTitle, content, setContent, savingStatus, versionsKey, saveCurrent, getSeq } = useSaveNote({
    note,
    hasKeyWrapper,
    isReadOnly,
    isEncryptionConversionActive,
    viewingAt: versionCreatedAt,
  });

  const displayedBody = isReadOnly ? versionContent : content;
  const exportUnavailableReason = isEncryptionConversionActive
    ? t('noteExport.conversion')
    : note?.body_locked || note?.body_error
      ? t('noteExport.locked')
      : isReadOnly && versionError
        ? t('noteExport.versionError')
        : loading || (isReadOnly && versionLoading) || displayedBody === null
          ? t('noteExport.loading')
          : undefined;
  const exportSnapshot: NoteExportSnapshot | null = note && !exportUnavailableReason && displayedBody !== null
    ? {
      title,
      body: displayedBody,
      contentType: note.content_type || 'TEXT',
      encrypted: Boolean(note.encrypted),
      version: viewingVersion,
    }
    : null;

  const handleToggleVersionsPanel = useCallback(() => {
    setIsVersionsPanelOpen(prev => !prev);
  }, []);

  const handleOpenVersion = useCallback((version: number) => {
    setViewingVersion(version);
  }, []);

  const handleBackToLatest = useCallback(() => {
    setViewingVersion(null);
  }, []);

  // Manage note tab lifecycle
  useNoteTab({
    uuid,
    noteTitle: note?.title,
    isLoaded: !!note && !loading,
    hasError: !!error || (!loading && !note)
  });

  // When a note with a key wrapper finishes loading, eagerly resolve and
  // cache the note key so encryption/decryption works without user interaction.
  // Silently skips when master key is not in memory (user must unlock first).
  useEffect(() => {
    if (note?.id && hasKeyWrapper) {
      void resolveNoteKey(note.id);
    }
  }, [note?.id, hasKeyWrapper]);

  const { setHasToolbarToggle } = useFullscreenStore();

  useEffect(() => {
    setHasToolbarToggle(true);
    return () => setHasToolbarToggle(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const target = document.getElementById('header-portal-target');
    setHeaderPortalTarget(target);

    const subheaderTarget = document.getElementById('subheader-sticky-portal');
    setSubheaderStickyPortal(subheaderTarget);
  }, []);

  const toolbarPortalRefCallback = (node: HTMLDivElement | null) => {
    if (node) {
      setToolbarPortalTarget(node);
    }
  };

  return (
    <>
      <Head>
        <title>{loading ? 'Loading...' : error ? 'Error' : (title || 'Untitled Note')}</title>
      </Head>

      {loading && (
        <div className="fixed inset-0 flex items-center justify-center">
          <Spinner size={50} enableTheme />
        </div>
      )}

      <div className="flex flex-col h-full relative">
        {!loading && (error || !note) && (
          <NoteError error={error || undefined} />
        )}

        {!loading && note && (
          <NoteProvider
            savingStatus={savingStatus}
            noteId={note.id ?? null}
            initialHasKeyWrapper={hasKeyWrapper}
            isVersionsPanelOpen={isVersionsPanelOpen}
            onToggleVersionsPanel={handleToggleVersionsPanel}
            isReadOnly={isReadOnly}
            viewingVersion={viewingVersion}
            onBackToLatest={handleBackToLatest}
            note={note}
            currentContent={content ?? ''}
            saveCurrent={saveCurrent}
            getSeq={getSeq}
            onEncryptionComplete={completeEncryptionConversion}
            isEncryptionConversionActive={isEncryptionConversionActive}
            onEncryptionConversionActiveChange={setEncryptionConversionActive}
          >
            {headerPortalTarget && createPortal(
              <>
                <InlineEditableField
                  value={title}
                  onChange={setTitle}
                  placeholder="Untitled"
                  className="text-lg font-semibold min-w-[120px] max-w-[180px] sm:max-w-[300px] md:max-w-[500px] px-3 py-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors overflow-hidden text-ellipsis whitespace-nowrap"
                  multiline={false}
                  maxLength={48}
                />
                {(note.tags ?? []).length > 0 && (
                  <div className="ml-2 hidden sm:flex items-center">
                    <NoteTagsDisplay tags={note.tags ?? []} maxVisible={5} />
                  </div>
                )}
              </>,
              headerPortalTarget
            )}
            {subheaderStickyPortal && createPortal(
              <div className="flex w-full items-start gap-1 px-4">
                <div ref={toolbarPortalRefCallback} className="min-w-0 flex-1 py-2" />
                <div className="py-2">
                  <NoteExportMenu noteId={note.id ?? null} snapshot={exportSnapshot} unavailableReason={exportUnavailableReason} />
                </div>
              </div>,
              subheaderStickyPortal
            )}

            {/* Flex row: editor takes remaining width, panel slides in from the right */}
            <div className="flex flex-1 overflow-hidden min-h-0">
              <div
                className="flex-1 min-w-0 p-4 overflow-auto pb-16"
                style={{ backgroundColor: note.color || '#fff' }}
              >
                {note.body_locked ? (
                  <div className="flex flex-col items-start gap-3 p-6 text-sm text-gray-500 dark:text-gray-300" role="status">
                    <span>{note.body_error || 'This encrypted note is locked. Your title and tags remain available.'}</span>
                    <button
                      type="button"
                      onClick={requestUnlock}
                      className="rounded-md bg-blue-600 px-3 py-1.5 font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
                    >
                      Enter master password
                    </button>
                  </div>
                ) : isReadOnly && (versionLoading || versionError) ? (
                  <p role="status" className="p-3 text-sm text-gray-500">{versionError || t('noteExport.loading')}</p>
                ) : displayedBody !== null && <NoteContent
                  note={note}
                  content={displayedBody}
                  onChange={setContent}
                  autoFocus={!loading && !!note && !isReadOnly}
                  toolbarPortalTarget={toolbarPortalTarget}
                />}
              </div>

              <VersionsPanel
                noteId={note.id ?? null}
                isOpen={isVersionsPanelOpen}
                onClose={handleToggleVersionsPanel}
                refreshKey={versionsKey}
                viewingContentId={viewingVersion}
                onOpenVersion={handleOpenVersion}
              />
            </div>
          </NoteProvider>
        )}
        <BottomBar />
      </div>
    </>
  );
};

Note.layout = (page: React.ReactNode) => <AppLayout gutterless={true} footer={false} children={page} />;
export default Note;
